/**
 * Generated React/TSX artifact scoped to source document effective from 1 May 2026.
 *
 * This file is intended to be copied into a React client if needed.
 */
import React from 'react';
import { REPOSIT_PRIVACY_POLICY } from '../../../../constants/links';

export enum MaximumPolicyCover {
  TEN_THOUSAND_POUNDS = 'TEN_THOUSAND_POUNDS',
  FIVE_THOUSAND_POUNDS = 'FIVE_THOUSAND_POUNDS',
}

export interface TenantTermsProps {
  maximumPolicyCover?: MaximumPolicyCover;
}

const documentCss =
  '@page {\n  size: A4;\n  margin: 16mm 12mm 18mm;\n}\n\nbody.tenant-terms-document-host {\n  margin: 0;\n  padding: 0;\n}\n\n.tenant-terms-document {\n  box-sizing: border-box;\n  color: #3a315e;\n  font-size: 12pt;\n  font-family: "Poppins", sans-serif;\n  -webkit-font-smoothing: antialiased;\n  -moz-osx-font-smoothing: grayscale;\n  margin: 0;\n  padding: 0 12pt;\n}\n\n.tenant-terms-document *,\n.tenant-terms-document *::before,\n.tenant-terms-document *::after {\n  box-sizing: inherit;\n}\n\n.tenant-terms-document p {\n  line-height: 1.5;\n  margin: 0 0 12pt;\n}\n\n.tenant-terms-document strong,\n.tenant-terms-document b {\n  font-weight: 700;\n}\n\n.tenant-terms-document em,\n.tenant-terms-document i {\n  font-style: italic;\n}\n\n.tenant-terms-document .document-logo {\n  text-align: center;\n  margin: 0 0 16pt;\n}\n\n.tenant-terms-document .document-logo img {\n  max-width: 180px;\n  height: auto;\n}\n\n.tenant-terms-document h1 {\n  font-size: 14pt;\n  font-weight: 700;\n  margin-top: 12pt;\n  margin-bottom: 30pt;\n  text-align: center;\n  line-height: 1.5;\n}\n\n.tenant-terms-document h2 {\n  font-size: 12pt;\n  font-weight: 700;\n  margin-top: 14pt;\n  margin-bottom: 8pt;\n  line-height: 1.5;\n}\n\n.tenant-terms-document h3 {\n  font-size: 12pt;\n  font-weight: 700;\n  margin-top: 14pt;\n  margin-bottom: 8pt;\n  line-height: 1.5;\n}\n\n.tenant-terms-document .document-subtitle {\n  text-align: center;\n}\n\n.tenant-terms-document .underlined-warning {\n  text-decoration: underline;\n}\n\n.tenant-terms-document .underlined-label {\n  text-decoration: underline;\n}\n\n.tenant-terms-document ul,\n.tenant-terms-document ol {\n  list-style-position: outside;\n  margin: 0 0 12pt;\n  padding-left: 24pt;\n}\n\n.tenant-terms-document ul {\n  list-style-type: disc;\n}\n\n.tenant-terms-document ol {\n  list-style-type: decimal;\n}\n\n.tenant-terms-document ol.alpha-list {\n  list-style-type: lower-alpha;\n}\n\n.tenant-terms-document li {\n  display: list-item;\n  margin-top: 10pt;\n  line-height: 1.5;\n}\n\n.tenant-terms-document li ul {\n  list-style: lower-alpha;\n}\n\n.tenant-terms-document a {\n  font-weight: 500;\n  color: #3a315e;\n  text-decoration: none;\n}\n\n.tenant-terms-document table {\n  border-collapse: collapse;\n  width: 100%;\n  font-size: 10pt;\n  text-align: left;\n  margin-bottom: 16pt;\n  table-layout: fixed;\n}\n\n.tenant-terms-document th,\n.tenant-terms-document td {\n  border: 1px solid #3a315e;\n  padding: 8px;\n  vertical-align: top;\n  overflow-wrap: break-word;\n}\n\n.tenant-terms-document .key-terms-box {\n  border: 1px solid #3a315e;\n  margin: 0 0 16pt;\n}\n\n.tenant-terms-document .key-terms-box td,\n.tenant-terms-document .key-terms-box th {\n  border: none;\n  padding: 8px;\n}\n\n.tenant-terms-document .tenant-signatories {\n  display: flex;\n  margin-top: 24pt;\n  width: 100%;\n}\n\n.tenant-terms-document .tenant-signatories__col {\n  flex: 1 auto;\n  width: 50%;\n}\n\n.tenant-terms-document .tenant-signatories p {\n  margin-bottom: 10pt;\n}\n\n.tenant-terms-document .tenant-signatories__meta {\n  margin: 0 0 4pt;\n  padding: 0;\n}\n';

/**
 * Mirrors the scalar placeholder replacement used in the HTML template with a
 * typed runtime formatter for the generated React component.
 */
const formatMaximumPolicyCover = (
  maximumPolicyCover: MaximumPolicyCover,
): string => {
  switch (maximumPolicyCover) {
    case MaximumPolicyCover.TEN_THOUSAND_POUNDS:
      return '10,000';
    case MaximumPolicyCover.FIVE_THOUSAND_POUNDS:
    default:
      return '5,000';
  }
};

const TenantTerms = ({
  maximumPolicyCover = MaximumPolicyCover.FIVE_THOUSAND_POUNDS,
}: TenantTermsProps) => {
  const formattedMaximumPolicyCover =
    formatMaximumPolicyCover(maximumPolicyCover);

  return (
    <>
      <style>{documentCss}</style>
      <div className="tenant-terms-document">
        <p className="document-logo">
          <img
            src="data:image/png;base64,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"
            alt="Reposit logo"
          />
        </p>
        <h1>Reposit Group Limited </h1>
        <h2 className="document-subtitle">Tenant Terms &amp; Conditions </h2>
        <p>These terms are effective from 22 April 2026. </p>
        <h2>These terms and conditions apply to Tenants using our services </h2>
        <p>
          These are the terms and conditions (“<strong>Conditions</strong>”)
          upon which we provide our services. We will ask you to accept these
          Conditions when you sign-up to use our services on our{' '}
          <a href="http://www.reposit.co.uk/">website.</a>{' '}
          <strong className="underlined-warning">
            If you do not agree to comply with these Conditions, do not use our
            services.
          </strong>{' '}
          Please contact us if you have questions.{' '}
        </p>
        <h2>About us and how to contact us </h2>
        <p>
          We are Reposit Group Limited t/a Reposit (“<strong>Reposit</strong>”).
          We are a company registered in England &amp; Wales. Our registered
          office is located at 128 City Road, London, EC1V 2NX and our
          registered number is 09581330. We are registered by the Financial
          Conduct Authority with registration number 844985.{' '}
        </p>
        <p>
          If you need to contact us, please email us at{' '}
          <strong>hello@reposit.co.uk</strong> or fill in the Contact Us form on
          our website.{' '}
        </p>
        <p>
          In these Conditions, where we use “<strong>Reposit</strong>”{' '}
          <strong>"we"</strong>, <strong>"our" </strong>and{' '}
          <strong>"us"</strong>, we are referring to Reposit.{' '}
        </p>
        <h2>About you and how we will contact you </h2>
        <p>
          When we use words like <strong>"you" </strong>or{' '}
          <strong>"your" </strong>in these Conditions, we are referring to you,
          an individual who is looking to use our services in relation to
          accommodation which you are renting or hoping to rent as domestic
          accommodation and not for business purposes. If you are looking to
          rent accommodation to use for business purposes these Conditions do
          not apply and you may not use our services. If we need to contact you,
          we will use the contact details which you provide to us when you
          register to use our services on our website.{' '}
        </p>
        <table className="bordered-table key-terms-box">
          <tbody>
            <tr>
              <td colSpan={2}>
                <p>
                  <strong>IMPORTANT TERMS: </strong>
                </p>
                <p>
                  To help you understand how the Reposit Product works we have
                  summarised some of the important, key terms below. The summary
                  does not reflect all of the terms and conditions which apply
                  between us, and you can read these in full below this summary
                  box.{' '}
                </p>
                <p>
                  <strong>Reposit Product</strong>
                </p>
                <p>
                  In relation to the Reposit Product, you may cancel and claim a
                  full refund during the 14day cooling off period (provided your
                  Tenancy Agreement has not yet started).{' '}
                </p>
                <ul>
                  <li>
                    You have the right to cancel and claim a full refund during
                    the 14-day cooling off period, which starts on the Purchase
                    Date, provided that your Tenancy Agreement has not yet
                    started.{' '}
                  </li>
                  <li>
                    If you do this, we will notify your landlord that your
                    Reposit Product has been cancelled, and they may require you
                    to pay a tenancy deposit. (see Clause 11 “Cancelling the
                    Agreement” for details).{' '}
                  </li>
                </ul>
                <p>
                  Additional rights to cancel after the end of the cooling off
                  period but before the Tenancy Agreement starts:
                </p>
                <ul>
                  <li>
                    After the end of the 14-day cooling off period, and provided
                    that your Tenancy Agreement has not yet started, you may
                    cancel our agreement and claim a full refund with your
                    landlord’s consent. We will ask you to provide proof that
                    your landlord has agreed to the cancellation.
                  </li>
                  <li>
                    If you do this, we will notify your landlord that your
                    Reposit Product has been cancelled, and they may request you
                    to pay a tenancy deposit. (see Clause 11 “Cancelling the
                    Agreement” for details).{' '}
                  </li>
                </ul>
                <p className="underlined-warning">
                  Once your Tenancy Agreement has started, you cannot cancel our
                  agreement and the Fee is non-refundable.{' '}
                </p>
                <p>How the Reposit Product works:</p>
                <ul>
                  <li>
                    The Reposit Product allows you to rent without having to pay
                    a tenancy deposit (which normally amounts to 5 weeks’ worth
                    of rent) at the start of your tenancy.{' '}
                  </li>
                  <li>
                    If you prefer to pay a tenancy deposit instead of using the
                    Reposit Product, you can agree to do this with your landlord
                    or their appointed agent.{' '}
                  </li>
                  <li>
                    The cost of the Reposit Product is an upfront fee equal to
                    one week’s worth of rent, subject to a minimum of £150. This
                    is non-refundable (except for where you cancel the agreement
                    (see above for where this is allowed)) and cannot be offset
                    against the value of any Charges (see below, but Charges
                    cover things like unpaid rent, cleaning costs, costs to
                    repair damage which you have caused, re-letting fees (where
                    applicable)) at the end of tenancy. For tenancies that
                    continue for 12 months or longer, you will be required to
                    pay us an annual fee of £30 per tenancy payable on each
                    anniversary of the start date of your tenancy.
                  </li>
                  <li>
                    With the Reposit Product, you remain fully responsible for
                    paying for the cost of repairing damage you have caused to
                    the property, as well as any rent arrears or for other
                    financial loss suffered by your landlord. Your tenancy
                    agreement will set out full details of what you are
                    responsible for.{' '}
                  </li>
                  <li>
                    With the Reposit Product, the maximum amount that your
                    landlord can claim is the lower of 8 weeks’ worth of rent or
                    £{formattedMaximumPolicyCover}. However, if you owe them
                    more than this under your tenancy agreement, your landlord
                    may take action to recover the additional sums from you
                    directly.{' '}
                  </li>
                  <li>
                    You will be notified by Reposit by email and/or SMS text of
                    any Charges raised by your landlord at the end of your
                    tenancy.{' '}
                    <strong className="underlined-warning">
                      It is important that you respond promptly to the
                      notification through your Reposit account on the Platform,
                      and in any event you must respond to it through your
                      Reposit account on the Platform within 7 calendar days of
                      the notification.
                    </strong>{' '}
                  </li>
                  <li>
                    We do not accept responses to Charges if you send them by
                    e-mail or any other communication method, so please ensure
                    you use the Platform to respond to Charges. We will send you
                    a reminder of such Charges by email and/or SMS text
                    approximately 4 calendar days and 6 calendar days after the
                    Charges have been raised, in each case if you have not
                    responded to the notification or reminder (as applicable)
                    through your Reposit account on the Platform. In certain
                    situations, we may also create an automated call to remind
                    you.{' '}
                  </li>
                  <li className="underlined-warning">
                    <strong>Finally, if </strong>you
                    <strong>
                      {' '}
                      do not respond through your Reposit account on the
                      Platform within 7 calendar days of the original
                      notification, you will be deemed to have accepted the
                      Charges in full and we will take steps to recover the
                      amount owed from you (see Clause 9 for full details).
                    </strong>{' '}
                  </li>
                  <li>
                    If you do not agree with the Charges, you will have the
                    opportunity to contest them with your landlord through your
                    Reposit account on the Platform and, if they remain
                    unsettled, raise a formal dispute with an independent and
                    impartial third party adjudicator, which we will facilitate.
                    To raise a formal dispute through adjudication, a fee of £60
                    must be paid. This adjudication fee will be refunded to you
                    in full if the adjudicator considers your dispute to be
                    valid. Clause 9 (Charges raised by the Landlord) sets out
                    the Charges process in full. The Adjudicator’s decision,
                    save in the case of manifest error, shall be final and
                    binding on both you and the Landlord, save that you may
                    choose to dispute the decision in court.{' '}
                  </li>
                  <li>
                    If you are renting with other people on the same tenancy
                    agreement, you are responsible for all Charges made by the
                    Landlord (including any share of the Charges due from the
                    other Tenants, if they fail to pay themselves).
                  </li>
                  <li>
                    During your tenancy, you are required to look after the
                    Accommodation and communicate with your Landlord (or the
                    letting agent) regarding any issues that may arise. We are
                    not responsible for any communications or resolving any
                    issues.{' '}
                  </li>
                </ul>
                <p>
                  <strong>Payments to us </strong>
                </p>
                <p>
                  You must pay us the Fees for the duration of the Agreement.
                  Fees are payable in addition to any Charges which might be
                  payable to your Landlord (see Clause 8 (Payment) and Clause 9
                  (Charges raised by Landlord)).
                </p>
                <ul>
                  <li>
                    By providing us with payment card details, you authorise us
                    to take further payments from you using these payment card
                    details in accordance with the Continuous Payment Authority
                    as set out in detail at Clause 6 of these terms below. For
                    clarity, we will only use this authorisation where you owe
                    us money and you have failed to respond to our reasonable
                    attempts to contact you. See Clause 6 (Continuous Payment
                    Authority) for more details, including how to cancel this
                    authorisation.{' '}
                  </li>
                  <li>
                    If you are having difficulty paying, please contact us.{' '}
                  </li>
                </ul>
                <p>
                  <strong>
                    We limit our liability under these Conditions{' '}
                  </strong>
                </p>
                <ul>
                  <li>
                    Clause 13 (Our Liability) explains what we will and will not
                    be liable for in the event of a dispute or issue arising
                    under the Agreement.{' '}
                  </li>
                </ul>
                <p>
                  <strong>
                    How our Deposit Payment and Registration Service Works
                  </strong>
                </p>
                <p>
                  If your Landlord (or their agent) uses our Deposit Payment and
                  Registration Service, we may facilitate your payment and
                  registration of that deposit as set out in these Conditions,
                  if your Landlord (or their agent) does not have this
                  integration, and you wish to pay a traditional deposit in relation to
                  your Tenancy, please arrange to pay the traditional deposit directly
                  with your Landlord (or their agent).{' '}
                </p>
                <p>
                  Under the Deposit Payment and Registration Service we can
                  facilitate your payment of a deposit into a government backed
                  tenancy deposit protection scheme. To do this we use we use a
                  third party payment processor Plaid Financial Limited (company
                  number 11103959, and having its registered office at Techspace
                  Level 4, 25 Worship Street, London, United Kingdom, EC2A 2DX)
                  and you will be required to accept their terms and conditions
                  before payment can be made. We will at no time hold your
                  deposit.
                </p>
                <p>
                  You are responsible for ensuring all payment details are
                  correct and that the Deposit has been transferred to the
                  Deposit Scheme Provider successfully.
                </p>
                <p>
                  Once you have paid a Deposit into a Deposit Scheme you have no
                  rights to cancel and claim a refund.{' '}
                </p>
                <p>
                  Following payment of the Deposit, this will then be held and
                  administered by the Deposit Scheme Provider in accordance with
                  the terms of the Deposit Scheme and applicable tenancy
                  laws.{' '}
                </p>
                <p>
                  Any information about your Deposit, the Deposit Scheme or
                  Deposit Scheme Provider is provided or made available to you
                  by the Landlord (or their agent) and we may display this
                  information on our Platform on the Landlord’s (or their
                  agent’s) behalf. We have no responsibility or liability for
                  the contents of any Deposit, Deposit Scheme or Deposit
                  Provider information or any of that information’s compliance
                  with applicable laws and should you have any questions,
                  concerns or complaints in relation to any information provided
                  you should raise this directly with the Landlord (or their
                  agent).{' '}
                  <strong>
                    Further details about our Deposit Payment and Registration
                    Services are set out in Schedule 1.{' '}
                  </strong>
                </p>
              </td>
            </tr>
            <tr>
              <td colSpan={2}>
                <p>
                  <strong>
                    Where your Landlord (or their agent) uses our Deposit
                    Payment and Registration Service, clauses 2, 3(b), 3(c),
                    3(d), 3(j), 3(k), 3(m), 4, 5, 6, 7(b), 7(d), 8(a), 9, 10, 11
                    and 12 will not apply to the payment and registration of
                    your Deposit.
                  </strong>
                </p>
                <p>
                  We have included this summary and paragraph headings to help
                  you navigate this document. But that’s all they are there for.
                  They shouldn’t be treated as having any impact at all on how
                  the wording of any of these Conditions should be
                  interpreted.{' '}
                </p>
                <p>
                  We may amend these terms and conditions to reflect changes in
                  laws and regulations or to make minor adjustments and
                  improvements. If we want to make any other changes and these
                  have a significant impact on the Conditions, we will notify
                  you in advance and you can discuss any questions with our
                  customer support team and terminate the Agreement if you do
                  not agree with the changes.{' '}
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h3>1. Definitions which we use in these Conditions </h3>
        <p>
          We will use a number of words and phrases repeatedly in these
          Conditions so, to make things easier, we will define what we mean when
          we use them here:{' '}
        </p>
        <p>
          <strong>Accommodation </strong>means the rental accommodation
          specified in the Tenancy Agreement and in relation to which the Fee
          has been paid or is payable.{' '}
        </p>
        <p>
          <strong>Agreement </strong>means the legal agreement between you and
          us, based on these Conditions and excluding any other Terms and
          Conditions or other documents.{' '}
        </p>
        <p>
          <strong>Annual Fee: </strong>the fee payable by the Tenant for using
          our Services in respect of the Tenancy Agreement as explained to you
          when you signed up for our Service, which is payable on each
          anniversary of the start date of the Tenancy Agreement.{' '}
        </p>
        <p>
          <strong>Applicable Legislation </strong>means any statute, statutory
          provision, statutory instrument, law, direction notice, rule or order
          made under any statute, any regulation or bye-law or requirement of
          any local authority or of any statutory undertaker who has
          jurisdiction with regard to the tenancy the subject of the Charge and
          any of the Pre-Tenancy Documents and the Tenancy Agreement.{' '}
        </p>
        <p>
          <strong>Charge </strong>means the cost of any loss or damage suffered
          by the Landlord relating to your Default, that the Landlord may submit
          via their Reposit account after the end of the Rental Period, up to
          the lower value of an amount equivalent to eight weeks’ rent for the
          Accommodation or £{formattedMaximumPolicyCover}. The value of eight
          weeks’ rent is calculated based on the agreed rent as stated in the
          Tenancy Agreement and entered on our Platform. It cannot be amended
          once the Reposit Fee has been paid, and the Tenancy has
          commenced.{' '}
        </p>
        <p>
          <strong>Company Guarantor </strong>means any company, undertaking or
          other entity (excluding a natural person) that guarantees the Tenant’s
          obligations under the Tenancy Agreement.{' '}
        </p>
        <p>
          <strong>Default </strong>means any failure by you to:{' '}
        </p>
        <ol className="alpha-list">
          <li>
            pay the whole or any part of the rents, interest and any other sums
            due to, or recoverable by, the landlord under the Tenancy Agreement,
            whether or not reserved by way of rent, including any utility bills
            (whether included in the rent or where the Tenant has failed to pay
            directly), and which are outstanding at the expiry or termination of
            the Tenancy Agreement and where you have vacated the
            Accommodation;{' '}
          </li>
          <li>
            observe and perform the Tenant covenants and conditions for repair
            contained or referred to in the Tenancy Agreement (save for fair
            wear and tear);{' '}
          </li>
          <li>
            return the Accommodation to your Landlord at the end of the Tenancy
            Agreement in the same condition (save for fair wear and tear) and
            state of cleanliness as the Accommodation was in at the start of the
            Tenancy Agreement, resulting in cleaning costs being incurred by the
            Landlord;
          </li>
          <li>
            remove your property and/or unwanted or unneeded items or waste
            materials from the Accommodation at the end of the Tenancy
            Agreement, resulting in costs being incurred by the Landlord to
            remove, store and dispose of such property and/or unwanted or
            unneeded items or waste materials.{' '}
          </li>
        </ol>
        <p>
          <strong>Deposit </strong>means the tenancy deposit paid by you into a
          Deposit Scheme Provider’s Deposit Scheme in connection with your
          Tenancy Agreement for the Accommodation as agreed between you and the
          Landlord or their appointed Agent. This amount will be protected under
          the applicable Deposit Scheme.
        </p>
        <p>
          <strong>Deposit Payment and Registration Service</strong> means our
          facilitation of your payment and the subsequent registration of your
          Deposit to a Deposit Scheme Provider’s Deposit Scheme in relation to a
          specified Tenancy Agreement.
        </p>
        <p>
          <strong>Deposit Scheme </strong>means a government backed tenancy
          deposit protection scheme.
        </p>
        <p>
          <strong>Deposit Scheme Provider </strong>means the provider of a
          Deposit Scheme which we have partnered with from time to time to
          provide you with access to that Deposit Scheme.{' '}
        </p>
        <p>
          <strong>Fee </strong>means either the Nominal Reposit Fee or the
          Reposit Fee, as applicable. We will confirm the applicable Fees to you
          through our website as part of your sign-up process.{' '}
        </p>
        <p>
          <strong>Further Fees </strong>means any additional administrative fees
          due to us from you, including:{' '}
        </p>
        <ol className="alpha-list">
          <li>
            any third party set up fees that may apply and that will be
            confirmed to you as part of the sign-up process;
          </li>
          <li>
            any Annual Fees payable where a tenancy continues past the initial
            period of 12 months;{' '}
          </li>
          <li>
            any dispute fees payable by you where you raise a formal dispute in
            relation to your Tenancy Agreement;{' '}
          </li>
          <li>
            any fees payable where a new Tenant joins an existing tenancy during
            its term.{' '}
          </li>
        </ol>
        <p>
          <strong>Landlord </strong>means the landlord whose Accommodation you
          wish to rent or are renting under a Tenancy Agreement, or the
          landlord’s appointed representative.{' '}
        </p>
        <p>
          <strong>Nominal Reposit Fee </strong>means, where the Landlord pays
          the Reposit Fee on your behalf, a nominal fee to be paid by you to us
          for our Services. Where applicable, the amount of the Nominal Fee will
          be confirmed to you as part of the sign-up process.{' '}
        </p>
        <p>
          <strong>Notice of Variation </strong>means notice of variation of
          certain matters as provided by Article 1 of the Private Tenancies Act
          (Northern Ireland) 2022.{' '}
        </p>
        <p>
          <strong>Parties, Party </strong>means us and/or you, depending on the
          context.{' '}
        </p>
        <p>
          <strong>Platform</strong> means our online Reposit platform accessible
          via <a href="http://www.reposit.co.uk">www.reposit.co.uk</a> by
          clicking “Login”, which is made available by us to you as part of the
          Services.
        </p>
        <p>
          <strong>Purchase Date </strong>means (i) where you are the only Tenant
          renting the Accommodation, the date when the Fee has been paid; or
          (ii) where you are renting the Accommodation with other people, the
          date when everyone has paid their part of the Fee.{' '}
        </p>
        <p>
          <strong>Prescribed Deposit Information </strong>refers to information
          that must be sent to the Tenant about their Deposit in accordance with
          applicable law, including as applicable pursuant to the Housing Act
          2004.{' '}
        </p>
        <p>
          <strong>Pre-Tenancy Documents </strong>means:{' '}
        </p>
        <ol>
          <li>
            FOR PROPERTIES LOCATED IN ENGLAND: those documents as required by
            the Gas Safety (Installation and Use) Regulations 1998 as amended by
            the Gas Safety (Installation and Use) (Amendment) Regulations 2018
            and the Electrical Safety Standards in the Private Rented Sector and
            Social Rented Sector (England) Regulations 2020, namely the Gas
            Safety Certificate, the Electrical Installation Condition Report and
            the Energy Performance Certificate;{' '}
          </li>
          <li>
            FOR PROPERTIES LOCATED IN WALES: those documents as required by
            Renting Home (Wales) Act 2016 and Housing (Wales) Act 2014 (namely
            the Gas Safety Certificate and the Energy Performance Certificate at
            the date of these terms and conditions);{' '}
          </li>
          <li>
            FOR PROPERTIES LOCATED IN SCOTLAND those documents as required by
            Private Housing (Tenancies) (Scotland) Act 2016;{' '}
          </li>
          <li>
            FOR PROPERTIES LOCATED IN NORTHERN IRELAND those documents as
            required by: The Private Tenancies (Northern Ireland) Order 2006 and
            the Private Tenancies Act (Northern Ireland) 2022, namely the
            Tenancy Information Notice, Notice of Variation (if applicable), and
            Rent Receipts; The Energy Performance of Buildings (Certificates and
            Inspections) Regulations (Northern Ireland) 2008 as amended by the
            Energy Performance of Buildings (Certificates and Inspections)
            (Amendment) Regulations (Northern Ireland) 2013, namely the Energy
            Performance Certificate; The Gas Safety (Installation and Use)
            Regulations (NI) 2004, namely the Gas Safety Certificate; to be
            provided to the Tenant before the commencement of the tenancy.{' '}
          </li>
        </ol>
        <p>
          <strong>Rental Period </strong>means the period of time during which
          you rent the Accommodation from the Landlord.{' '}
        </p>
        <p>
          <strong>Rent Receipts </strong>means a rent receipt for payment in
          cash as provided by Article 3 of the Private Tenancies Act (Northern
          Ireland) 2022.{' '}
        </p>
        <p>
          <strong>Reposit Fee </strong>means the non-refundable fee payable by
          you under the Agreement for our Services. This fee is equal to one
          week’s worth of rent, subject to a minimum of £150, calculated based
          on the agreed rent as stated in the Tenancy Agreement and as entered
          on our platform. This fee is non-refundable, except for where you
          cancel the agreement (as per Clause 11), it cannot be amended once it
          has been paid and the Tenancy has commenced, and it cannot be offset
          against the value of any Charges at the end of tenancy.{' '}
        </p>
        <p>
          <strong>Reposit Product </strong>our tenancy security product as
          outlined in these terms as made available to Landlords and Tenants in
          relation to a Tenancy Agreement including (where applicable) a Reposit
          Switch, as further described in these Conditions
        </p>
        <p>
          <strong>Reposit Switch: </strong>has the meaning given in Clause
          10.{' '}
        </p>
        <p>
          <strong>Services </strong>means (i) the Reposit Product; (ii) the
          recovery of any Charges and/or Fees from you pursuant to the
          Agreement; and (iii) the specific services set out in Clause 7, in
          each case in return for payment of the Fees.{' '}
        </p>
        <p>
          <strong>Tenancy Agreement </strong>means:{' '}
        </p>
        <ol>
          <li>
            FOR PROPERTIES LOCATED IN ENGLAND: the Assured Periodic Tenancy
            Agreement,{' '}
          </li>
          <li>FOR PROPERTIES LOCATED IN WALES: the Occupation Contract, </li>
          <li>
            FOR PROPERTIES LOCATED IN SCOTLAND: the Private Residential Tenancy
            Agreement and such other documents in relation to the tenancy all
            signed by the Landlord and the Tenant,{' '}
          </li>
          <li>
            FOR PROPERTIES LOCATED IN NORTHERN IRELAND: The Private Tenancy
            Agreement,{' '}
          </li>
        </ol>
        <p>in each case entered into between the Tenant and Landlord. </p>
        <p>
          <strong>Tenant </strong>means a tenant (or tenants) that wishes to
          rent Accommodation from the Landlord, and who shall be making the
          Payment to us (unless the Landlord has agreed to make the Payment on
          its behalf) and, if applicable, the Further Payment.{' '}
        </p>
        <p>
          <strong>Tenant’s Guarantor </strong>means any person guaranteeing the
          obligations of the Tenant to the Landlord under the Tenancy Agreement
          and includes any Company Guarantor.{' '}
        </p>
        <p>
          <strong>Tenancy Information Notice </strong>means notice of the grant
          of tenancy as provided by Article 1 of the Private Tenancies Act
          (Northern Ireland) 2022.{' '}
        </p>
        <p>
          In these Conditions, for properties located in England we may refer to
          Acts of the United Kingdom Parliament. For properties located in
          Scotland we may refer to Acts of Scottish Parliament. For properties
          located in Northern Ireland we may refer to Acts and Orders of the
          Northern Ireland Assembly, and Acts of Parliament which also apply in
          Northern Ireland. For Accommodation located in Wales we may refer to
          Acts of the United Kingdom Parliament and Acts of Senedd Cymru. When
          we refer to these aforementioned Acts, you can take it that we are
          referring both to that Act and any regulations or subordinate
          legislation made under it. We are also referring to any amendments
          that may be made either to the Act in question or any such regulations
          and so on. If the Act is replaced by something else that takes its
          place, then, until we update the Conditions to take that into account,
          you can also take it that we are referring to whatever the new
          legislation is instead.{' '}
        </p>
        <p>
          Just for ease of drafting, we may use plural or singular and you can
          read singular as including plural and vice versa. When we refer to
          persons or people, this includes companies, unincorporated
          associations and partnerships.{' '}
        </p>
        <h3>2. Our Services </h3>
        <p>
          We will provide the Services to you until the Agreement expires or is
          terminated early by you or by us in accordance with the terms of the
          Agreement.{' '}
        </p>
        <p>
          If you already owe us any Fees or Charges we may prevent you from
          purchasing further Services from us unless and until the pre-existing
          debts are paid in full, along with any interest owed to us.{' '}
        </p>
        <h3>3. Your General Obligations to Us </h3>
        <p>You agree: </p>
        <ol>
          <li>
            Where you provide information to us (for example contact details or
            reference details), you will ensure it is complete and accurate and
            you confirm that you are allowed to share the information with
            us.{' '}
          </li>
          <li>
            That we and/or your Landlord can use the information which you
            provide to us to carry out reference checks on you with the purpose
            of confirming your identity and financial history. If we determine,
            acting reasonably, that the information provided by you is false,
            misleading or illegal, we may terminate the Agreement with immediate
            effect. If we terminate the Agreement under this section, you
            understand that you may be required to pay a tenancy deposit for the
            Accommodation to the Landlord;{' '}
          </li>
          <li>To pay the Fees promptly; </li>
          <li>
            And understand that we will be unable to provide the Services until
            the Fee has been paid in full;
          </li>
          <li>
            And understand that the Landlord may not proceed with the renting
            the Accommodation to you until the Fee has been paid in full or you
            have paid a tenancy deposit instead of the Fee;{' '}
          </li>
          <li>
            To promptly pay the Annual Fee to us on each anniversary of the
            start date of your Tenancy Agreement;{' '}
          </li>
          <li>To comply with all Applicable Legislation; </li>
          <li>
            To pay your rent to the Landlord on time and in full, to keep the
            Accommodation in a satisfactory condition and in accordance with the
            terms of the Tenancy Agreement and to report any issues with the
            Accommodation (such as repairs and faults) to the Landlord as soon
            as possible;{' '}
          </li>
          <li>
            To nominate a lead Tenant in cases of multiple occupancy, as
            appropriate, who is responsible for communicating with us
            (understanding that this does not excuse you from performing your
            obligations under the Agreement);{' '}
          </li>
          <li>
            That we may use the contact details you provide to communicate with
            you in relation to the Services we provide to you. Communication may
            be through various means, including but not limited to email, SMS
            (text messages), instant messaging services, telephone calls or
            physical mail. You acknowledge and agree to ensure the contact
            details you provide us are accurate and to promptly provide us with
            up-to-date contact details if your contact details change. You
            understand that failure to keep your contact details current may
            result in you missing important communications regarding your
            account or our Services;{' '}
          </li>
          <li>
            That you are jointly and severally liable with any other Tenant
            under the same Tenancy Agreement as you and we reserve the right to
            recover the full value of the Charge from you (on behalf of the
            Landlord) notwithstanding that there are other Tenants;{' '}
          </li>
          <li>
            That after the end of the Rental Period, we shall inform you, or the
            lead Tenant as appropriate, if the Landlord has made a Charge. Where
            a Charge has been made, you agree to comply with the terms of Clause
            9 (Charges raised by Landlord);{' '}
          </li>
          <li>
            That any payment by you of the Charge (including any alternative or
            reduced amount agreed to through the Charge process), is not
            necessarily a payment in full and final settlement of any claims the
            Landlord may have against you. Whilst you will not be required to
            pay any sums to the Landlord in excess of the Charge via your
            Reposit account on the Platform, this is without prejudice to any
            additional sums that the Landlord may be able recover from you
            outside of the Charges;{' '}
          </li>
          <li>At all material times to act in good faith towards us; </li>
          <li>
            To inform us immediately when the Rental Period ends for whatever
            reason, including if you have received a notice seeking possession
            from the Landlord (or for Scottish properties a notice to quit or
            notice of proceedings from the Landlord, or for Northern Irish
            properties a notice to quit).{' '}
          </li>
        </ol>
        <p>
          You acknowledge and agree that it is your decision (made freely) to
          enter into the Agreement as an alternative to paying a rental deposit
          to the Landlord. You understand and accept that we provide no
          assistance or advice regarding the Accommodation’s condition,
          presentation, marketing, advertising, Tenancy Agreements, termination,
          Accommodation queries or complaints. You will need to contact the
          Landlord directly with regards to those matters.{' '}
        </p>
        <p>
          You understand that if you fail to pay any Fees or Charges due under
          the Agreement that we may take legal action to recover these from you
          and that this may impact your credit rating.{' '}
        </p>
        <h3>4. You must notify us of changes to your Tenancy Agreement </h3>
        <p>
          If your Tenancy Agreement is extended (if applicable) or if it is
          terminated, you must tell us immediately. You understand that the
          Annual Fee is payable by you to us for as long as the Tenancy
          Agreement is active.{' '}
        </p>
        <p>
          If you used a Company Guarantor when purchasing the Reposit Product
          you must tell us if that Company Guarantor stops guaranteeing your
          obligations under your Tenancy or if the Company Guarantor
          changes.{' '}
        </p>
        <h3>
          5. You confirm that you are the payment cardholder or that you are
          authorised by the payment cardholder to use the payment card{' '}
        </h3>
        <p>
          If you use the Reposit Product and you provide payment card details to
          us to make a payment, you represent and warrant that you are the named
          cardholder or, if you are not a named cardholder on such payment card,
          that you have obtained the express authority of the cardholder to make
          such payment and that the cardholder understands and agrees to the
          Continuous Payment Authority described in Clause 6.{' '}
        </p>
        <p>
          You agree to indemnify us in full for and against all claims, costs,
          expenses or liabilities whatsoever and howsoever arising incurred or
          suffered by us including all legal expenses and other professional
          fees (together with any VAT thereon) in relation to any breach of the
          representation in this Clause 5.{' '}
        </p>
        <h3>
          6. Continuous Payment Authority when using the Reposit Product: by
          providing us with payment card details, you authorise us to use these
          details to take payments owed under these Conditions.{' '}
        </h3>
        <p>
          If you use the Reposit Product, by providing us with your debit or
          credit card details, you authorise us to use these details to take
          payment from you for the following fees and charges owed by you to us
          (“<strong>Continuous</strong> <strong>Payment Authority</strong>
          ”):{' '}
        </p>
        <p>
          Charges: where we have tried to contact you through your account about
          outstanding Charges and (i) you have failed to respond to our messages
          and (ii) you have failed to pay the outstanding Charges that we have
          tried to contact you about.{' '}
        </p>
        <p>
          Annual Fees: where we have tried to contact you through your account
          about outstanding Annual Fees and (i) you have failed to respond to
          our messages and (ii) you have failed to pay the outstanding Annual
          Fees that we have tried to contact you about.{' '}
        </p>
        <p>
          Payment Plan Instalments: where we have agreed to a payment plan with
          you for the repayment of Fees or Charges that you owe and one or more
          of the agreed instalments have become due or overdue.{' '}
        </p>
        <p>
          If you disagree with any Fees or Charges or if you are having
          difficulty paying, please contact us to discuss your options.{' '}
        </p>
        <p>
          <em>Cancelling the Continuous Payment Authority </em>
        </p>
        <p>
          You can cancel the Continuous Payment Authority by notifying us in
          writing at our registered office address (set out on page 1 of these
          Conditions) or your card issuer. If you cancel the Continuous Payment
          Authority and you owe money, we may charge you an additional fee of
          £100 to cover our additional administrative costs incurred in
          collecting the debt from you (by way of illustration, such
          administrative costs would include, but not be limited to, our costs
          of outsourcing to debt collection agencies). This administrative fee
          is payable in addition to the Fees or Charges which you owe.{' '}
        </p>
        <h3>7. Our Obligations to You </h3>
        <p>Subject to you complying with these Conditions, we will: </p>
        <ol>
          <li>
            Grant you access to the Platform, which allows you to sign-up and
            create an account, and use our Services, and if agreed with your
            Landlord use our Deposit Payment and Registration Services.{' '}
          </li>
          <li>Provide you with the Services.</li>
          <li>
            Collect payment of Fees and (where applicable) Charges and/or
            Further Fees from you, in addition to any other amounts due to us
            under these Conditions. Once payment of Charges is received, we will
            then settle these Charges with your Landlord on your behalf.{' '}
          </li>
          <li>
            Maintain records in relation to the Agreement and the Tenancy
            Agreement for the purpose of performing our obligations under the
            Agreement and our obligations to the Landlord.{' '}
          </li>
          <li>
            Facilitate communications between the Landlord and you in relation
            to any Charge. As set out in Clause 9 (Charges), this includes
            providing access to a third party independent and impartial
            adjudicator if a dispute arises.{' '}
          </li>
        </ol>
        <h3>8. Payment </h3>
        <p>
          To receive our Services, you (and each other Tenant, if you are
          renting with others) must pay the Fees and Further Fees in full using
          a credit or debit card via our website. Each Tenant is required to use
          a separate payment card when paying us. Once the Tenancy Agreement
          starts, the Reposit Fee is non-refundable. Prior to the start of the
          Tenancy Agreement you have some rights to cancel our agreement and
          claim a full refund – see Clause 11 for details of your rights.{' '}
        </p>
        <p>
          All sums payable under the Agreement are exclusive of VAT or other
          applicable sales tax (unless otherwise stated), which if payable shall
          be paid by you in addition.{' '}
        </p>
        <p>
          If you fail to make any payment due to us under the Agreement by the
          due date for payment, then, in addition to other remedies that we may
          have under these Conditions, you will pay interest on the overdue
          amount and our legal costs of recovering any sums due to us including
          any legal costs and reclaimable court fees. The interest will be at
          the rate of 4% per annum. Interest shall accrue on a daily basis from
          the due date until actual payment of the overdue amount (whether
          before or after judgment). You shall pay the interest together with
          the overdue amount.{' '}
        </p>
        <h3>9. Charges raised by the Landlord </h3>
        <p>
          <em>What happens if the Landlord raises a </em>Charge<em>? </em>
        </p>
        <p>
          We will share the details of the Charge with you via email and through
          your account on our Platform (“<strong>Charge Notification</strong>
          ”).{' '}
        </p>
        <p>
          You agree that the Landlord may share with us information about you
          and your tenancy for the purpose of verifying any Charge (such
          information may include your Tenancy Agreement, the result of
          reference checks and any other evidence to support the Charge).{' '}
        </p>
        <p>
          It is important that you follow the process below to either accept or
          dispute the Charge. If you do not respond to the Charge through your
          Reposit account on the Platform within 7 days of receiving the Charge
          Notification, we will assume you accept the Charges in full and will
          take steps to recover the amount owed from you.
        </p>
        <p>
          We only accept responses to Charges through your Reposit account on
          the Platform and do not accept responses by e-mail or any other
          communication method.{' '}
        </p>
        <p>
          <em>How do I accept the </em>Charge<em> and pay? </em>
        </p>
        <p>
          If you agree with the Charge, you must pay the Charge by using the
          payment function available through your account on our Platform.{' '}
        </p>
        <p>
          You agree that if we are not able to take payment using these payment
          methods then we may take payment using the Continuous Payment
          Authority and we may also take legal action to recover the Charge from
          you. If you are having difficulty paying, please contact us to discuss
          your options.{' '}
        </p>
        <p>
          <em>What do I do if I </em>disagree<em> with the Charge? </em>
        </p>
        <p>
          If you disagree with the Charge, you must follow the process set out
          on your Reposit account dashboard on the Platform to reject the Charge
          through the Platform within 7 days of receiving the Charge
          Notification. If you do not respond through the Platform within 7 days
          of receiving the Charge Notification, we will assume you accept the
          Charges in full and will take steps to recover the amount owed from
          you. You agree that if we do not hear from you then we may take
          payment using the Continuous Payment Authority and we may also take
          legal action to recover the Charge from you. If you are having
          difficulty paying, please contact us to discuss your options.{' '}
        </p>
        <p>
          Please note that if you have any outstanding debts with us, we may
          refuse any requests you make in the future to set up new Reposit
          Products with us or otherwise use our Services.{' '}
        </p>
        <p>
          If you disagree with the Charge, please provide evidence to support
          why you disagree with the Charge through your account on our Platform,
          you may also propose an alternative settlement amount to the Landlord
          (a “<strong>Proposed Settlement</strong>”). If you make a Proposed
          Settlement and your Landlord accepts, you agree to make payment
          immediately by using the payment function available through your
          account on the Platform.{' '}
        </p>
        <p>
          We will share the information you provide, along with your Proposed
          Settlement, with the Landlord. The Landlord will have 7 days to either
          accept your Proposed Settlement or to propose a final settlement back
          to you (“<strong>Final Settlement</strong>”).{' '}
        </p>
        <p>
          <em>Options for responding to a Final Settlement </em>
        </p>
        <p>
          If the Landlord proposes a Final Settlement, you have 7 days to accept
          or reject it. If you accept it, you will pay the Final Settlement
          amount using the payment functions available through your account on
          our Platform.{' '}
        </p>
        <p>
          If you want to reject the Final Settlement you must do so through your
          Reposit account on the Platform within 7 days or we will assume you
          have accepted it and will take steps to recover payment from you. You
          agree that if we do not hear from you then we may take payment using
          the Continuous Payment Authority and we may also take legal action to
          recover the Charge from you. If you are having difficulty paying,
          please contact us to discuss your options.{' '}
        </p>
        <p>
          If you reject the Final Settlement, you may ask us to refer the
          dispute to an independent adjudicator and take payment of the
          Adjudication Fee from you (see next paragraph).{' '}
        </p>
        <p>
          <em>Involving an independent </em>Adjudicator
        </p>
        <p>
          If you reject a Final Settlement you may raise a formal dispute, and
          request that we refer the matter to an independent and impartial third
          party adjudicator. To raise a formal dispute through adjudication, a
          fee of £60 must be paid to us (the “<strong>Adjudication Fee</strong>
          ”). Upon receipt of the Adjudication Fee we will refer the dispute,
          along with all evidence we have received from you or the Landlord to
          an independent and impartial adjudicator (“
          <strong>Adjudicator</strong>”) to resolve the dispute.{' '}
        </p>
        <p>
          If the Adjudicator reduces the Final Settlement the Adjudication Fee
          will be refunded to you in full (otherwise it is non-refundable).{' '}
        </p>
        <p>
          If the Adjudicator decides that you do owe the Landlord an amount
          (either the Final Settlement or an alternative amount stipulated by
          the Adjudicator):{' '}
        </p>
        <ol>
          <li>
            You agree to pay us through your account on the Platform within 3
            calendar days.{' '}
          </li>
          <li>
            If you fail to pay us and we are not able to contact you, you agree
            that we may take payment from you under the Continuous Payment
            Authority.{' '}
          </li>
          <li>
            You agree that if we are not able to take payment using these
            payment methods then we may take legal action to recover the Charge
            from you.{' '}
          </li>
        </ol>
        <p>
          {' '}
          We reserve the right to refer any disputes relating to a Charge to an
          Adjudicator for settlement. We may make such a referral in the event
          of a dispute, or for any other reason in our discretion.{' '}
        </p>
        <p>
          The Adjudicator’s decision, save in the case of manifest error, shall
          be final and binding on you and the Landlord, save that you may choose
          to dispute the decision in court.{' '}
        </p>
        <p>
          If you fail to pay for a Charge when due, we may make any claim in
          respect of such failure to pay on behalf of Reposit Group Limited
          and/or the Landlord including through the service of formal legal
          proceedings.{' '}
        </p>
        <p>
          Any notice and/or any documentation of any description (including
          those relating to legal proceedings and/or any document required to be
          served in accordance with the Civil Procedure Rules in England and
          Wales or Scotland or the Rules of the Court of Judicature (NI) 1980 in
          Northern Ireland given in connection with the Agreement) shall be sent
          and/or served upon you by e mail to the e mail address you have
          provided to us for this purpose and which is associated with your
          account with us (the e mail address which you use to sign into that
          account) and you agree to such notices and documents being served by e
          mail. Service will be deemed to have been effected at the time of
          transmission of the relevant email.{' '}
        </p>
        <p>
          This Clause 9 (Charges Raised by Landlord) shall survive termination
          of the Agreement. This means that your obligation to pay any Charge
          (or any reduced or alternative amount agreed following the Charge
          process) amount continues after your Tenancy Agreement ends.{' '}
        </p>
        <h3>10. Reposit Switch </h3>
        <p>
          If the eligibility criteria below apply to both you and the Landlord,
          you can start using the Services for an existing Tenancy Agreement
          (either during the term or at the renewal of an existing tenancy) to
          replace a tenancy deposit that has already been paid by you to your
          Landlord (“<strong>Reposit Switch</strong>”). You should liaise with
          your Landlord to arrange the return of your tenancy deposit.{' '}
        </p>
        <p>
          <em>Eligibility criteria: </em>
        </p>
        <ol className="alpha-list">
          <li>
            You and the Landlord must sign the Reposit Addendum to the relevant
            Tenancy Agreement which shall then be appended to form part of such
            Tenancy Agreement; and{' '}
          </li>
          <li>
            if the Rental Period commenced less than 6 months prior to the
            Reposit Switch start date, you must have passed referencing when you
            entered into the Tenancy Agreement in accordance with our
            referencing criteria; and{' '}
          </li>
          <li>
            you must not be or have been in rent arrears or agreed any rent
            payment holidays in the 6 months prior to the start date of the
            Reposit Switch, or since the start of the Rental Period (whichever
            is the shorter period). For the avoidance of doubt, “rent arrears”
            is defined as any rent payment made or still outstanding more than
            14 calendar days after its due date; and{' '}
          </li>
          <li>
            the rent must not have been increased by more than 10% in the 6
            months prior to the start date of the Reposit Switch; and
          </li>
          <li>
            you confirm that you are not aware of any material change to your
            Tenancy Agreement or to your circumstances (including but not
            limited to your financial position) which could reasonably be
            expected to affect your ability to comply with your obligations
            under the Tenancy Agreement, including the payment of rent and the
            proper care and maintenance of the Accommodation.{' '}
          </li>
        </ol>
        <h3>11. Cancelling the Agreement </h3>
        <p>
          <em>
            Unless the Tenancy Agreement has started, you have the legal right
            to cancel during the 14 day cooling off period starting from the
            Purchase Date.{' '}
          </em>
        </p>
        <p>
          The cooling off period automatically ends as soon as your Tenancy
          Agreement starts and so this means your right to cancel and claim a
          full refund ends when the Tenancy Agreement begins (if this is within
          the 14 day cooling off period).{' '}
        </p>
        <p>
          If you wish to cancel our agreement during the cooling off period you
          must let us know by emailing us on hello@reposit.co.uk or via the live
          chat functionality on our website and you confirm that you will also
          let your Landlord know that you have cancelled with us.{' '}
        </p>
        <p>
          In addition to your legal right to cancel during the cooling off
          period (explained above), you may cancel our agreement and claim a
          full refund if you have the landlord’s consent but only if the Tenancy
          Agreement has not yet started. If you wish to cancel our agreement you
          must let us know by emailing us on hello@reposit.co.uk or via the live
          chat functionality on our website. We will ask for proof that the
          Landlord has agreed to the cancellation.{' '}
        </p>
        <p>
          You understand that if you cancel the purchase of the Reposit Product,
          your Landlord may require you to pay a tenancy deposit instead.{' '}
        </p>
        <p>
          <em>The Fee is non-refundable once the Tenancy Agreement starts.</em>
        </p>
        <p>
          Once the Tenancy Agreement starts our agreement cannot be cancelled
          and the Fee is non-refundable in full or in part. Your rights to
          cancel are different from any rights which you may have to terminate
          (if we are in breach of the agreement, for example). See Clause 12 for
          details of when you or we can terminate this agreement.{' '}
        </p>
        <h3>12. Expiry and Termination of the Agreement </h3>
        <p>
          The Reposit Product will be valid from the start date of your Tenancy
          Agreement.{' '}
        </p>
        <p>
          The Reposit Product will expire, along with the Agreement, at the end
          of the Rental Period once any Charges (if applicable) have been
          settled with the Landlord. Where this applies the date of expiry of
          the Reposit Product and the Agreement will be the date on which we
          confirm to you that the Service has ended in relation to the specific
          Tenancy Agreement.{' '}
        </p>
        <p>
          The expiry of the Reposit Product does not affect our ongoing right to
          recover any debts or Charges from you which were incurred or relate to
          your acts or omissions prior to the expiry of the Reposit Product.
        </p>
        <p>
          Without affecting any other right or remedy available, either Party
          may terminate the Agreement with immediate effect by giving written
          notice to the other Party if:{' '}
        </p>
        <ol className="alpha-list">
          <li>
            the other Party fails to pay any amount due on the due date for
            payment and remains in default not less than 14 days after being
            notified in writing to make such payment;{' '}
          </li>
          <li>
            the other Party commits a material breach of these Conditions which
            is irremediable or if such breach is remediable fails to remedy that
            breach within a period of 14 days after being notified in writing to
            do so;{' '}
          </li>
          <li>
            the other Party repeatedly breaches any of these Conditions in such
            a manner as reasonably to justify the opinion that its conduct is
            inconsistent with it having the intention or ability to give effect
            to the Agreement.{' '}
          </li>
        </ol>
        <p>
          Termination or expiry of the Agreement does not limit or restrict: (i)
          our ability to recover any debts which you owe under the Agreement, or
          (ii) either our or your rights or remedies under the Agreement or
          which otherwise exist as a matter of law. If you owe us money at the
          point of termination/cancellation/expiry, you agree that we may take
          legal action to recover any debts from you.{' '}
        </p>
        <p>
          If the Agreement is cancelled or terminated for any reason and your
          Tenancy Agreement remains in place, if you were using the Reposit
          Product you understand that your Landlord may ask you to provide a
          deposit to them or to their agent or by using our Deposit Payment and
          Registration Services.{' '}
        </p>
        <h3>12A Deposit Payment and Registration Services</h3>
        <p>
          The Conditions of Schedule 1 apply to the Deposit Payment and
          Registration Services.
        </p>
        <h3>13. Our Liability </h3>
        <p>YOUR ATTENTION IS PARTICULARLY DRAWN TO THIS PARAGRAPH. </p>
        <p>
          Nothing in these Conditions shall limit or exclude our liability for
          death or personal injury caused by our negligence, or the negligence
          of our employees, agents or subcontractors or fraud or fraudulent
          misrepresentation.{' '}
        </p>
        <p>
          You acknowledge and accept that we have no responsibility or liability
          for the Tenancy Agreement or your relationship with the Landlord,
          except for as it is expressly described in these Terms. We do not make
          any representations, warranties or other promises concerning the
          Accommodation, the Tenancy Agreement, or your ability to rent that
          Accommodation,{' '}
        </p>
        <p>We are not responsible for any loss which is: </p>
        <ul>
          <li>
            Unexpected or not obvious that it would happen when we entered into
            the Agreement (so, in the law, the loss was unforeseeable).{' '}
          </li>
          <li>Caused by a delay outside of our reasonable control. </li>
          <li>
            Something which you could have avoided by taking reasonable
            action.{' '}
          </li>
          <li>
            Any business loss, relating to your use of our Services in
            connection with a business, trade or profession.{' '}
          </li>
        </ul>
        <p>
          Our total liability to you in respect of all other losses arising
          under or in connection with the Contract, whether in contract, tort
          (including negligence), breach of statutory duty, or otherwise, shall
          in no circumstances exceed a sum equal to the Fees or where no Fees
          are payable a sum equal to 30% of the amount of the Deposit.
        </p>
        <p>This Clause 13 shall survive termination of the Agreement. </p>
        <h3>14. No responsibility for events beyond our reasonable control </h3>
        <p>
          No Party shall be liable or be deemed to be in breach of its
          obligations if its delay in performing, or failure to perform, was due
          to any cause beyond its reasonable control (an “Event of Force
          Majeure”).{' '}
        </p>
        <p>
          We agree to give notice to each other upon becoming aware of an Event
          of Force Majeure. The notice shall contain details of the Event of
          Force Majeure circumstances. If an Event of Force Majeure continues
          for more than four weeks, the Party not in default may terminate the
          Agreement.{' '}
        </p>
        <h3>15. Data Protection </h3>
        <p>
          How we use any personal data you give us is set out in our Privacy
          Policy:{' '}
          <a href={REPOSIT_PRIVACY_POLICY}>
            {REPOSIT_PRIVACY_POLICY}
          </a>{' '}
          and in Table 1 at the end of this document where we state our
          controller and processor activities.{' '}
        </p>
        <p>
          You agree that we may share your personal data with our insurers,
          brokers, payment processors, Deposit Scheme Providers, Landlord’s (and
          their agents) and other parties with a direct interest in relation and
          ancillary to the Agreement and the provision of the Services.{' '}
        </p>
        <ul>
          <li>
            Sharing data: Under data protection laws such as the UK Data
            Protection Act 2018 and the UK GDPR we may need to receive
            information from the Landlord or their appointed Agent in order to
            carry out our Services. Where we act as a controller (that is where
            we determine the purpose and means of processing personal data) our
            lawful basis for processing personal data is for the performance of
            a contract that is to carry out the Service to you. Further details
            of the information shared can be found in Table 1 at the end of this
            document.{' '}
          </li>
        </ul>
        <p>
          Where we act as a processor (that is we carry out the instructions of
          the Landlord or their appointed Agent) the lawful basis for processing
          personal data is determined by them. Please see Table 2 at the end of
          this document for the types of information we process and can receive
          from the Landlord or their appointed Agent.{' '}
        </p>
        <h3>16. General Information </h3>
        <p>
          We may amend these Conditions to reflect changes in laws and
          regulations or to make minor adjustments and improvements. If we want
          to make any other changes and these have a significant impact on the
          Conditions, we will notify you in advance and you can discuss any
          questions with our customer services team and terminate the Agreement
          if you do not agree with the changes.{' '}
        </p>
        <p>
          If either Party waives a breach or default of any of these Conditions
          by the other, this will not be deemed to be a waiver of any further
          breach of the same or other provisions. Likewise, if either Party
          delays or does not exercise any right, power or privilege that it has
          or may have under these Conditions, this will not be deemed to be a
          waiver of any breach or default.{' '}
        </p>
        <p>
          If any court or administrative body of competent jurisdiction finds
          that any of these Conditions or part of them is invalid or
          unenforceable, the rest will continue to apply.{' '}
        </p>
        <p>The purchase of the Reposit Product is not transferable. </p>
        <p>
          Any notices to be given by you under the Agreement must be made in
          writing (which may be by email) and addressed to Reposit Group
          Limited, 128 City Road, London, EC1V 2NX.{' '}
        </p>
        <p>
          <strong>
            FOR ACCOMMODATION LOCATED IN ENGLAND AND WALES AND NORTHERN IRELAND:
          </strong>{' '}
          Nobody other than you and us has any right under the Contracts (Rights
          of Third Parties) Act 1999 to enforce or enjoy the benefit of any term
          of the Agreement provided that this does not affect any right or
          remedy of the third party, which exists or is available apart from
          that Act.
        </p>
        <p>
          <strong>FOR ACCOMMODATION LOCATED IN ENGLAND AND WALES:</strong> These
          Conditions shall be governed by and construed in accordance with
          English law and the Parties hereto agree to submit to the exclusive
          jurisdiction of the English Courts.{' '}
        </p>
        <p>
          <strong>FOR ACCOMMODATION LOCATED IN NORTHERN IRELAND:</strong> These
          Conditions shall be governed by and construed in accordance with
          Northern Irish law and the Parties hereto agree to submit to the
          exclusive jurisdiction of the Northern Irish Courts.{' '}
        </p>
        <p>
          <strong>FOR ACCOMMODATION LOCATED IN SCOTLAND</strong>:{' '}
        </p>
        <ol className="alpha-list">
          <li>
            Nobody other than the Parties has any right under the Contract
            (Third Parties Rights) (Scotland) Act 2017 to enforce or enjoy the
            benefit of any term of the Agreement provided that this does not
            affect any right or remedy of the third party, which exists or is
            available apart from that Act.{' '}
          </li>
          <li>
            These Conditions shall be governed by and construed in accordance
            with Scottish law and the Parties hereto agree to submit to the
            exclusive jurisdiction of the Scottish Courts.{' '}
          </li>
        </ol>
        <h2>Schedule 1 – Deposit Payment and Registration Services</h2>
        <p>
          <strong>
            Where your Landlord (or their agent) uses our Deposit Payment and
            Registration Service, clauses 2, 3(b), 3(c), 3(d), 3(j), 3(k), 3(m),
            4, 5, 6, 7(b), 7(d), 8(a), 9, 10, 11 and 12 will not apply to the
            payment and registration of your Deposit.
          </strong>{' '}
          <strong>
            All other Conditions apply to your use of the Deposit Payment and
            Registration Services in addition to the following:
          </strong>
        </p>
        <p>
          To use our Deposit Payment and Registration Service, you must pay the
          Deposit through the Platform. Payment of the Deposit will be made
          directly to the Deposit Scheme Provider. Upon payment being made we
          will register that Deposit with the Deposit Scheme Provider. To make
          payment of the Deposit we use a third party payment processor Plaid
          Financial Limited (company number 11103959, and having its registered
          office at Techspace Level 4, 25 Worship Street, London, United
          Kingdom, EC2A 2DX) and payment will be subject to their terms and
          conditions. We will at no time hold your Deposit.
        </p>
        <p>
          You are responsible for ensuring all payment details are correct and
          that the Deposit has been transferred to the Deposit Scheme Provider
          successfully.{' '}
        </p>
        <p>
          Once you have paid a Deposit into a Deposit Scheme you have no rights
          to cancel and claim a refund.{' '}
        </p>
        <p>
          Once payment of the Deposit is made this will be held by and
          administered by the Deposit Scheme Provider in accordance with the
          terms of the Deposit Scheme.{' '}
        </p>
        <p>
          You understand and agree that once a Deposit is paid this is will not
          be returned to you except where agreed with the Landlord at the end of
          the Tenancy Agreement and in accordance with the terms of the relevant
          Deposit Scheme and applicable tenancy laws.
        </p>
        <p>
          Registration of the Deposit with the Deposit Scheme Provider
          constitutes completion of the Deposit Payment and Registration
          Services for that Deposit.{' '}
        </p>
        <p>
          Where you provide a Deposit, the Landlord provides you with Prescribed
          Deposit Information. We may make this information available through
          the Platform on the Landlord’s (or their agent’s) behalf. We are not
          liable and have no responsibility for the contents of the Prescribed
          Deposit Information or for checking whether the Prescribed Deposit
          Information complies with applicable laws. You should raise any
          questions, concerns or complaints about Prescribed Deposit Information
          directly with your Landlord (or their agent).{' '}
        </p>
        <p>
          Either Party may terminate the Deposit Payment and Registration
          Service with immediate effect by giving written notice to the other
          Party if:{' '}
        </p>
        <ol className="alpha-list">
          <li>
            the other Party commits a material breach of these Conditions which
            is irremediable or (if such breach is remediable) fails to remedy
            that breach within a period of 14 days after being notified in
            writing to do so;{' '}
          </li>
          <li>
            the other Party repeatedly breaches any of these Conditions in such
            a manner as reasonably to justify the opinion that its conduct is
            inconsistent with it having the intention or ability to give effect
            to the Agreement.{' '}
          </li>
        </ol>
        <p>
          Termination or expiry of the Deposit Payment and Registration Service
          does not limit or restrict: (i) our ability to recover any debts which
          you owe under the Agreement, or (ii) either our or your rights or
          remedies under the Agreement or which otherwise exist as a matter of
          law.{' '}
        </p>
        <h2>Table 1: Particulars of processing </h2>
        <table className="bordered-table">
          <tbody>
            <tr>
              <td>
                <p>Scope, Nature and Purpose of processing</p>
              </td>
              <td>
                <p>
                  To provide the Services or Deposit Payment and Registration
                  Services (as applicable) set out in the Agreement
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>Categories of data subject </p>
              </td>
              <td>
                <p>Landlords or Agents, Tenants, Tenant guarantors </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  Types of personal data (see the table below for a description
                  of each type of personal data)
                </p>
              </td>
              <td>
                <ul>
                  <li>Log-In Information</li>
                  <li>Contact Information</li>
                  <li>Tenant Reposit Information </li>
                  <li>Tenant Sign Up Information </li>
                  <li>Tenant Payment Information</li>
                  <li>Complaint Information </li>
                  <li>Website User Information</li>
                  <li>Tenant Technical Information</li>
                  <li>Offer Reposit Information</li>
                  <li>Reposit Creation Information</li>
                  <li>Agent or Landlord Charge Evidence</li>
                  <li>Tenant Evidence</li>
                  <li>ADR Evidence</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td>
                <p>Duration of the processing</p>
              </td>
              <td>
                <p>
                  For the duration of the Services or Deposit Payment and
                  Registration Services (as applicable).{' '}
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h2>
          Table 2: Description of information that Reposit can process as a
          Controller or Processor for Tenants.{' '}
        </h2>
        <p>
          <span className="underlined-label">Controller:</span> Where we act as
          a controller of your personal data our lawful basis for processing
          personal data is for the performance of contract with you or for our
          legitimate interests or the interests of a third party where those
          interest do not override your fundamental rights and freedom.{' '}
        </p>
        <p>
          <span className="underlined-label">Processor:</span> Where we act a
          processor on behalf of the Agents or Landlords we act upon their
          instructions only and therefore we do not set the legal basis for
          processing personal data.{' '}
        </p>
        <table className="bordered-table">
          <tbody>
            <tr>
              <td>
                <p>
                  <strong>Reposit as a Controller </strong>
                </p>
              </td>
              <td>
                <p>
                  <strong>Reposit as a Processor </strong>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  Log-in details and information you provide as an account
                  holder when you register with the platform (“
                  <strong>Log-In Information</strong>”).
                </p>
              </td>
              <td>
                <p>
                  <strong>On behalf of Agents or Landlords: </strong>When using
                  our “Offer Reposit” feature the Agent or Landlord will enter
                  contact information for the Tenant as well as some basic
                  information about their tenancy such as the property address
                  and rental price per month so that the offer to use the
                  Reposit Product can be sent to the Tenant (“
                  <strong>Offer Reposit Information</strong>”).
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  Contact information we collect from you as an account holder,
                  for example name, address, contact address, telephone number
                  and email address provided to us by you or by someone who you
                  have given consent to be added to our Platform (“
                  <strong>Contact Information</strong>”).
                </p>
              </td>
              <td>
                <p>
                  <strong>On behalf of Agents or Landlords: </strong>When
                  creating a Reposit Product the Agent or Landlord will enter
                  information about the Tenant and the Tenant’s Guarantor (when
                  applicable) so that we can invite the Tenant to sign up and
                  pay for their Reposit Product and communicate with the
                  Tenant’s Guarantor as required (“
                  <strong>Reposit Creation Information</strong>”).{' '}
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  A record of the Services or Deposit Payment and Registration
                  Services you have used through the Platform and the
                  information relating to each individual Reposit Product or
                  Deposit, including but not limited to tenancy address, tenancy
                  details, Tenant details, landlord details, Charges raised and
                  any outstanding Fees, Further Fees or Charges owed. (“
                  <strong>Tenant Reposit Information</strong>”).
                </p>
              </td>
              <td>
                <p>
                  <strong>On behalf of Agents or Landlords: </strong>Documentary
                  evidence submitted by the Agent or Landlord to substantiate
                  end of tenancy Charges submitted via the Platform. This
                  evidence will include core tenancy documents to evidence that
                  there was a valid and eligible tenancy in place along with
                  other supporting evidence for each Charge item.
                </p>
                <p>Such evidence will typically include: </p>
                <ul>
                  <li>Tenancy Agreement </li>
                  <li>
                    Referencing information – e.g. identity verification, credit
                    checking and affordability checks
                  </li>
                  <li>
                    Result of Tenant reference checks IE referencing result
                    reports
                  </li>
                  <li>Initial inventory </li>
                  <li>Mid-tenancy inspection reports</li>
                  <li>End of tenancy check out report </li>
                  <li>Rent statement </li>
                  <li>Guarantor information (when applicable)</li>
                  <li>Invoices / quotes / receipts for repair of damages </li>
                  <li>
                    Communication with the Tenant discussing damages or chasing
                    rent arrears
                  </li>
                  <li>
                    Communication with contractors or suppliers discussing the
                    detail of damages or dilapidations and the work required.
                  </li>
                </ul>
                <p>
                  (“<strong>Agent or Landlord Charge Evidence</strong>”)
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  When a Tenant receives the invitation from Reposit to sign up
                  and pay the Fee they will enter details about themselves such
                  as their name, email, phone, gender, date of birth and current
                  address. The Tenant will also accept these terms and
                  conditions and we will record the date and time this was done
                  along with their IP address at the time (“
                  <strong>Tenant Sign Up Information</strong>”).
                </p>
              </td>
              <td>
                <p>
                  <strong>On behalf of Tenants: </strong>When responding to end
                  of tenancy Charges the Tenant will upload their own evidence
                  relating to themselves as the Tenant and their tenancy. This
                  can be documentary or photographic evidence and may include
                  records of communication with the Agent or Landlord.{' '}
                </p>
                <p>
                  (“<strong>Tenant Evidence</strong>”)
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  Information relating to payment transactions which is
                  collected where we collect payment for our Services (the Fee
                  or Further Fees) or for the repayment of a Charge or for
                  payment of the Deposit (Note: we do not collect credit card
                  information which is sent directly from the user to our
                  payment processor) (“
                  <strong>Tenant Payment Information</strong>”).
                </p>
              </td>
              <td>
                <p>
                  <strong>On behalf of Agents and Landlords or Tenants:</strong>{' '}
                  In case of a formal dispute in connection with the Reposit
                  Product Reposit will collate and submit all evidence received
                  from the Tenant and or the Agent or Landlord to our
                  adjudication partner who will produce their adjudication
                  report and submit this to us to forward on to the Tenant and
                  Agent or Landlord.
                </p>
                <p>
                  (“<strong>ADR Evidence</strong>”)
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  Information we may require from a Tenant, when they report a
                  problem or complaint (“<strong>Complaint Information</strong>
                  ”).
                </p>
              </td>
              <td></td>
            </tr>
            <tr>
              <td>
                <p>
                  Details of any user’s visit to the website at{' '}
                  <a href="http://www.reposit.co.uk">www.reposit.co.uk</a> or a
                  record of any correspondence between you and us and other
                  interactions with the website (“
                  <strong>Website User Information</strong>”).
                </p>
              </td>
              <td></td>
            </tr>
            <tr>
              <td>
                <p>
                  A Tenant’s use of our Platform and the actions they take on
                  the Platform, including but not limited to actions taken when
                  managing a Reposit Product or Deposit or their account or user
                  information, responding to Charges, notifications sent and
                  received, resources and pages accessed and any searches made
                  (“<strong>Technical Information</strong>”).
                </p>
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

export default TenantTerms;
